/**
 * EstimateNameModal — name + details for a logged-in user's estimate.
 *
 * create mode (first save): estimate name, "This project is" (required, centered
 * radio), project location (country required; region required when the country
 * has subdivisions; city/postal optional) pre-filled from the user's profile and
 * overridable, plus "I Am A" when the profile is missing it.
 * rename mode: estimate name + "This project is".
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Tag } from 'lucide-react';
import { UserProfile } from '../../services/api';
import { UserCategory } from '../../data/userCategories';
import { GeoCountry, regionsFor, regionLabelFor } from '../../data/regions';

export interface EstimateDetails {
  name: string;
  projectType: string; // 'commercial' | 'residential'
  country: string;
  region: string;
  city: string;
  postalCode: string;
  userCategory: string; // only meaningful when needUserCategory
}

interface Props {
  open: boolean;
  mode: 'create' | 'rename';
  initialName: string;
  initialProjectType: string;
  profile: UserProfile | null;
  /** Profile is missing "I Am A" → collect it inline. */
  needUserCategory: boolean;
  categories: UserCategory[];
  geo: GeoCountry[];
  saving: boolean;
  error?: string | null;
  onSave: (v: EstimateDetails) => void;
  onClose: () => void;
}

export const EstimateNameModal: React.FC<Props> = ({
  open, mode, initialName, initialProjectType, profile, needUserCategory,
  categories, geo, saving, error, onSave, onClose,
}) => {
  const { t } = useTranslation('results');
  const [name, setName] = useState(initialName);
  const [projectType, setProjectType] = useState(initialProjectType);
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [userCategory, setUserCategory] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // (Re)seed when the modal opens.
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setProjectType(initialProjectType);
    setCountry(profile?.country || '');
    setRegion(profile?.region || '');
    setCity(profile?.city || '');
    setPostalCode(profile?.postalCode || '');
    setUserCategory('');
    setLocalError(null);
    const timer = setTimeout(() => nameRef.current?.select(), 50);
    return () => clearTimeout(timer);
  }, [open, initialName, initialProjectType, profile]);

  const regions = useMemo(() => regionsFor(geo, country), [geo, country]);
  const regionLabel = regionLabelFor(geo, country);
  const regionRequired = regions.length > 0;
  const isCreate = mode === 'create';

  if (!open) return null;

  const submit = () => {
    if (saving) return;
    const trimmedName = name.trim();
    if (!trimmedName) { setLocalError(t('save.errors.name')); return; }
    if (!projectType) { setLocalError(t('save.errors.projectType')); return; }
    if (isCreate) {
      if (needUserCategory && !userCategory.trim()) { setLocalError(t('account:info.errors.category')); return; }
      if (!country.trim()) { setLocalError(t('account:info.errors.country')); return; }
      // regionLabel is admin-managed DB text ("State"/"Province") — Phase 3.
      if (regionRequired && !region.trim()) { setLocalError(t('account:info.errors.region', { region: regionLabel.toLowerCase() })); return; }
    }
    setLocalError(null);
    onSave({ name: trimmedName, projectType, country, region, city, postalCode, userCategory });
  };

  const setCountrySafe = (v: string) => { setCountry(v); setRegion(''); };

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(58, 75, 92, 0.8)' }}
      onClick={() => { if (mode === 'rename' && !saving) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
              <Tag size={18} /> {isCreate ? t('save.title') : t('save.editTitle')}
            </h5>
            {mode === 'rename' && (
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} aria-label={t('auth:close')} />
            )}
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label small fw-semibold">{t('save.nameLabel')} <span className="text-danger">*</span></label>
              <input
                ref={nameRef}
                type="text"
                className="form-control"
                placeholder={t('save.namePlaceholder')}
                value={name}
                maxLength={255}
                disabled={saving}
                onChange={e => setName(e.target.value)}
              />
            </div>

            {/* This project is — required, centered */}
            <div className="mb-3 text-center">
              <label className="form-label small fw-semibold d-block">{t('save.projectIs')} <span className="text-danger">*</span></label>
              <div className="d-inline-flex gap-4 justify-content-center">
                {(['commercial', 'residential'] as const).map(v => (
                  <div className="form-check" key={v}>
                    <input
                      type="radio"
                      id={`enm-${v}`}
                      name="enm-projectType"
                      className="form-check-input"
                      value={v}
                      checked={projectType === v}
                      disabled={saving}
                      onChange={() => setProjectType(v)}
                    />
                    {/* text-capitalize dropped: the value was rendered raw and
                        CSS-cased, which cannot work once it is translated. */}
                    <label className="form-check-label" htmlFor={`enm-${v}`}>{t(`save.projectTypes.${v}`)}</label>
                  </div>
                ))}
              </div>
            </div>

            {isCreate && (
              <>
                {needUserCategory && (
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">{t('account:info.iAmA')} <span className="text-danger">*</span></label>
                    <select className="form-select" value={userCategory} disabled={saving} onChange={e => setUserCategory(e.target.value)}>
                      <option value="" disabled>{t('account:info.select')}</option>
                      {/* Stored value stays English; only the label is translated. */}
                      {categories.map(c => <option key={c.slug} value={c.nameEn}>{c.name}</option>)}
                    </select>
                  </div>
                )}

                <div className="row g-2 mb-2">
                  <div className="col-12 col-sm-6">
                    <label className="form-label small fw-semibold">{t('account:info.country')} <span className="text-danger">*</span></label>
                    <select className="form-select" value={country} disabled={saving} onChange={e => setCountrySafe(e.target.value)}>
                      <option value="" disabled>{t('account:info.select')}</option>
                      {geo.map(c => <option key={c.isoCode || c.nameEn} value={c.nameEn}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label small fw-semibold">
                      {regionLabel}{regionRequired && <span className="text-danger"> *</span>}
                    </label>
                    <select className="form-select" value={region} disabled={saving || !country || !regionRequired} onChange={e => setRegion(e.target.value)}>
                      <option value="">{country
                        ? (regionRequired ? t('account:info.selectRegion', { region: regionLabel.toLowerCase() }) : t('account:info.notApplicable'))
                        : t('account:info.selectCountryFirst')}</option>
                      {regions.map(r => <option key={r.code} value={r.valueEn}>{r.value}</option>)}
                    </select>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label small fw-semibold">{t('account:info.city')}</label>
                    <input className="form-control" value={city} disabled={saving} onChange={e => setCity(e.target.value)} placeholder={t('account:info.optional')} />
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label small fw-semibold">{t('account:info.postalCode')}</label>
                    <input className="form-control" value={postalCode} disabled={saving} onChange={e => setPostalCode(e.target.value)} placeholder={t('account:info.optional')} />
                  </div>
                </div>
              </>
            )}

            {(localError || error) && <div className="alert alert-danger py-2 small mt-2 mb-0">{localError || error}</div>}
          </div>
          <div className="modal-footer">
            {mode === 'rename' && (
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>{t('auth:cancel')}</button>
            )}
            <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={submit} disabled={saving}>
              {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {isCreate ? t('save.submit') : t('save.saveChanges')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimateNameModal;
